import React from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isJSONArray} from 'util/Types';

import ArrayEditor from '../editors/ArrayEditor';
// eslint-disable-next-line import-access/jsdoc -- ShapeEditor is package-private but ui-schemas is part of the same package
import ShapeEditor from '../editors/ShapeEditor';
import {
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  BACKEND_FIELD_GENERATOR_CONFIG,
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
} from '../StepConfigEditorTypes';

// Exported for testing.
export function HamiltonRuntimeVariablesEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const runtimeVarsShape = getConfigShapeAtConfigShapePath(['runtimeVariables'], configShape, config);

  // runtimeVariables flips between a typed shape (object value, for a method with variables) and an
  // array (the free-form fallback). Switching from a variables method to one without can briefly
  // leave the previous method's object in config while the shape is already the array, and
  // ArrayEditor asserts the value is an array. Normalize a stale non-array value to [] in an effect
  // (never during render — mutating config while rendering warns and can loop).
  const currentValue = maybeGetValueFromConfig(config, ['runtimeVariables']);
  const isStaleNonArray =
    runtimeVarsShape.kind === 'array' && currentValue != null && !isJSONArray(currentValue);
  React.useEffect(() => {
    if (isStaleNonArray) {
      onChangeConfig(config, ['runtimeVariables'], []);
    }
  }, [isStaleNonArray, config, onChangeConfig]);

  // When a method is selected the backend introspects its global variables (GET_METHOD_VARIABLES)
  // and returns a typed shape; render one field per variable.
  if (runtimeVarsShape.kind === 'shape') {
    const shapeWithMeta = runtimeVarsShape as Record<string, unknown>;
    const fieldOrder =
      (shapeWithMeta.__fieldOrder as Array<string> | undefined) ?? Object.keys(runtimeVarsShape.fields);
    const displayNames = (shapeWithMeta.__displayNames as Record<string, string> | undefined) ?? {};

    if (fieldOrder.length === 0) {
      return <></>;
    }

    return (
      <ShapeEditor
        configShape={configShape}
        config={config}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
        uiSchema={{
          [FIELD_ORDER_UI_SCHEMA_KEY]: fieldOrder.map((name) => `runtimeVariables_${name}`),
          ...Object.fromEntries(
            fieldOrder.map((name) => [
              `runtimeVariables_${name}`,
              {
                // GET_METHOD_VARIABLES reports only the variable name, which is the field label.
                displayName: displayNames[name] ?? name,
                configShapePath: ['runtimeVariables', name],
              },
            ])
          ),
        }}
      />
    );
  }

  // Fallback: a free-form list of name/value pairs, used before a method is selected or when the
  // method's variables could not be introspected. Render nothing until the stale-value effect above
  // has normalized a non-array value, so ArrayEditor never sees a non-array.
  if (runtimeVarsShape.kind === 'array') {
    if (isStaleNonArray) {
      return <></>;
    }
    return (
      <ArrayEditor
        config={config}
        configShape={runtimeVarsShape}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
        configShapePath={['runtimeVariables']}
        addButtonAltText="Add variable"
        elementUISchema={{
          [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'value'],
          [NUM_COLUMNS_KEY]: 2,
          name: {
            displayName: 'Variable',
            configShapePath: ['name'],
          },
          value: {
            displayName: 'Value',
            configShapePath: ['value'],
          },
        }}
      />
    );
  }

  return <></>;
}

export const RUN_HAMILTON_METHOD_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_HAMILTON_METHOD,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'connectionId',
    'methodName',
    'outputDirectory',
    'simulation',
    'runtimeVariables',
  ],
  [BACKEND_FIELD_GENERATOR_CONFIG]: {triggerFields: ['connectionId', 'methodName']},
  connectionId: {
    displayName: 'Connection',
    infoText: 'The instrument connection to the Hamilton VENUS system.',
    configShapePath: ['connectionId'],
  },
  methodName: {
    displayName: 'Method',
    infoText: 'The Hamilton method to run. Selecting a method will populate any available runtime variables.',
    configShapePath: ['methodName'],
  },
  outputDirectory: {
    displayName: 'Output directory',
    infoText:
      'Optional. A directory on the Hamilton system to watch for output files. Files created here ' +
      'during the run are collected and attached to the step as outputs.',
    configShapePath: ['outputDirectory'],
  },
  simulation: {
    displayName: 'Simulation mode',
    infoText: 'Run the method in VENUS simulation mode without moving hardware.',
    configShapePath: ['simulation'],
  },
  runtimeVariables: {
    displayName: 'Runtime variables',
    infoText: 'Method variables sent to the VENUS system at run time.',
    [CUSTOM_COMPONENT_KEY]: HamiltonRuntimeVariablesEditor,
  },
};
